import { ArrowRight, BookOpenText, Eye, EyeOff, LockKeyhole } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { api } from "./api";
import { AppShell } from "./components/AppShell";
import { ErrorState, LoadingState } from "./components/common";
import { AppProvider } from "./context";
import { CreatorsPage } from "./pages/CreatorsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DataQualityPage } from "./pages/DataQualityPage";
import { ExpensesPage } from "./pages/ExpensesPage";
import { ImportsPage } from "./pages/ImportsPage";
import { OrdersPage } from "./pages/OrdersPage";
import { SettingsPage } from "./pages/SettingsPage";
import type { Bootstrap, User } from "./types";

export function App() {
  const [session, setSession] = useState<User | null | undefined>(undefined);
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshBootstrap = useCallback(async () => {
    const result = await api<Bootstrap>("/api/bootstrap");
    setBootstrap(result);
    setError(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void api<{ user: User }>("/api/session")
      .then((result) => {
        if (!cancelled) setSession(result.user);
      })
      .catch(() => {
        if (!cancelled) setSession(null);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    void api<Bootstrap>("/api/bootstrap")
      .then((result) => { if (!cancelled) setBootstrap(result); })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => { cancelled = true; };
  }, [session]);

  if (session === undefined) return <div className="app-loading"><LoadingState label="正在校验安全会话" /></div>;
  if (!session) return <LoginPage onLogin={setSession} />;
  if (error) return <div className="app-loading"><ErrorState message={error} onRetry={() => window.location.reload()} /></div>;
  if (!bootstrap) return <div className="app-loading"><LoadingState label="正在加载店铺与账期" /></div>;
  if (!bootstrap.stores.length && bootstrap.user.role !== "admin") return <div className="app-loading"><ErrorState message="当前没有可用的Amazon或TikTok店铺，请联系管理员先完成设置。" /></div>;

  return (
    <AppProvider bootstrap={bootstrap} refreshBootstrap={refreshBootstrap}>
      <AppShell>
        <Routes>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/creators" element={<CreatorsPage />} />
          <Route path="/imports" element={<ImportsPage />} />
          <Route path="/quality" element={<DataQualityPage />} />
          <Route path="/settings" element={bootstrap.user.role === "admin" ? <SettingsPage /> : <Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to={bootstrap.stores.length ? "/dashboard" : "/settings"} replace />} />
        </Routes>
      </AppShell>
    </AppProvider>
  );
}

function LoginPage({ onLogin }: { onLogin: (user: User) => void }) {
  const [account, setAccount] = useState("admin");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await api<{ user: User }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email: account, password }) });
      onLogin(result.user);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-story">
        <div className="login-brand"><span><BookOpenText size={21} /></span><strong>清账</strong></div>
        <div className="login-copy">
          <span className="eyebrow">Amazon / TikTok 利润核算</span>
          <h1>销售很多，<br />最后到底赚多少。</h1>
          <p>把订单收入、履约成本、广告、达人和平台结算放进同一本利润账，逐层看清产品、推广和最终净利润。</p>
        </div>
        <div className="login-ledger" aria-hidden="true">
          <div><span>应收金额</span><b>574.26</b></div>
          <i />
          <div><span>实收金额</span><b>574.26</b></div>
          <i />
          <div><span>对账结果</span><b>已对平</b></div>
        </div>
      </section>
      <section className="login-panel">
        <form onSubmit={submit}>
          <div className="login-lock"><LockKeyhole size={20} /></div>
          <h2>登录跨境利润账本</h2>
          <p>使用公司分配的账号进入。</p>
          <label className="field"><span>账号</span><input autoComplete="username" autoCapitalize="none" spellCheck={false} type="text" value={account} onChange={(event) => setAccount(event.target.value)} required /></label>
          <label className="field password-field"><span>密码</span><span><input autoComplete="current-password" type={visible ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} required /><button type="button" className="icon-button" aria-label={visible ? "隐藏密码" : "显示密码"} onClick={() => setVisible((value) => !value)}>{visible ? <EyeOff size={18} /> : <Eye size={18} />}</button></span></label>
          {error ? <div className="form-error" role="alert">{error}</div> : null}
          <button className="button primary login-submit" disabled={submitting}>{submitting ? "正在验证…" : <><span>进入利润账本</span><ArrowRight size={18} /></>}</button>
          <small className="security-note">会话 8 小时后自动失效 · 操作全程留痕</small>
        </form>
      </section>
    </main>
  );
}
